import { Injectable } from '@angular/core';
import { AuthService, UserAuth } from './auth.service';
import { UserDb } from './user-db.service';
import { getDeviceId, isAppStandalone, isCapacitorNative, isE2ETest, isPlatformAndroid, isPlatformIOS } from 'environments/environment-helpers';
import { getUnversionedRelayOrigin } from 'util/backend-origin';
import { peekLocalDatabaseEngineWithoutLatching } from './local-database-engine';
import { AppVersion } from 'app/version';
import Bowser from 'bowser';
import { encodeTelemetryEvent, encodeTelemetryUserState, isTeamTestAccountEmail, isTestTrafficSession, IS_TEST_TRAFFIC_PROPERTY, TelemetryData } from '@cf-shared/telemetry-shared';
import { SettingsService } from './settings.service';
import { AppEnvironment } from 'environments/environment';
import { CloudFunctionsService } from './cloud-functions.service';
import { DateTime } from 'luxon';
import { UrlQueryService } from './url-query-service';
import { LoggingService } from './logging.service';
import { SessionHealth } from './session-health';
import { buildUserProfileProps, isIntakeDone, shouldMarkFoodLoggedEver, shouldTrackAuthenticatedSessionStart } from './telemetry-profile-props';
import { RefinerClient, RefinerSessionContext } from './refiner-client';
import { buildUiLanguageTelemetryProps, UiLanguageTelemetryProps } from 'i18n/ui-language';
import { BOOT_LOCALE } from 'i18n/i18n.service';
import { TelemetryQueue } from './telemetry-service-queue';
import { TelemetryTransport } from './telemetry-transport';
import { ConnectionState, connectionStateChangedEvent, OnlineStateService } from './online-state.service';
import { PubSubService } from './pubsub.service';
import mixpanel, { BeforeSendHookPayload, Config } from "mixpanel-browser";
import { redactCredentialsInText } from 'app/breadcrumb-redaction';
import { ConfigDbService } from './config-db.service';
import { ConfigKey } from './config-keys';
import {
  TelemetryMode,
  resolveTelemetryMode,
  telemetryModeSends,
  readTelemetryLocalOptIn,
  describeTelemetryMode,
  resolveReplayPercent,
} from './telemetry-mode';
import {
  cacheTrackingFields,
  hasCacheableTrackingFields,
  loadCachedTracking,
} from './tracking-cache';



/*
Mixpanel forgot to declare `record_heatmap_data` in TypeScript interface: https://github.com/mixpanel/mixpanel-js/issues/499
remove this with next SDK update which should have a fix.
*/
interface MixpanelExtendedConfig extends Partial<Config> {
  record_heatmap_data?: boolean;
}

/**
 * Take the community sign-in credential out of an event's page address before it is sent.
 *
 * Why this exists on top of clearing the address bar (#5151). The SDK's own click and
 * page-leave events read `$current_url` from the live address, and neither passes through
 * any code of ours, so nothing we log or track controls them. Clearing the address in
 * `SsoService.parseUrl` is the fix; this is the second line of defence, for an event that
 * fires before the clearing or after some future change puts the pair back.
 *
 * It uses the SAME rule Sentry already applies to breadcrumbs, so the two answers cannot
 * drift apart: only an address matching one of the credential rules in `breadcrumb-redaction.ts`
 * loses its query (on our own pages, the `sso=`+`sig=` pair or a sign-in link token), and every
 * other event keeps its full address for diagnostics.
 *
 * MUST ALWAYS RETURN THE PAYLOAD: the SDK reads a hook that answers nothing as a failure,
 * reports an error and drops the send. Every path below returns it.
 *
 * Deliberately NOT wrapped in a try/catch. Nothing inside can throw on the types the guard
 * admits — a regular expression over a string, and an assignment onto a bag the SDK itself
 * just built. And if some future change did make it throw, losing that one event is the
 * right way to fail for a rule whose job is to keep a credential in: swallowing the error
 * would send the address unredacted, which is the outcome this function exists to prevent.
 */
function redactCredentialsInMixpanelEvent(payload: BeforeSendHookPayload): BeforeSendHookPayload {
  const url = payload?.properties?.['$current_url'];
  if (typeof url === 'string') {
    payload.properties['$current_url'] = redactCredentialsInText(url);
  }
  return payload;
}

@Injectable()
export class TelemetryService {
  analyticsDisabled = true;

  private sessionId: string;

  // This page load's answer to "is this session being recorded?", as a promise so that the
  // question is asked exactly once however many callers arrive. See
  // maybeStartSessionRecording, which explains why more than one of them does.
  private sessionRecordingDecision: Promise<boolean> | undefined = undefined;

  // whether this build may touch Mixpanel at all. decided once in the constructor,
  // before the SDK is initialised, and never reconsidered: flipping it mid-session
  // would leave the SDK half set-up. see telemetry-mode.ts.
  private readonly telemetryMode: TelemetryMode;

  // set when the Mixpanel SDK threw while starting up. the SDK is then left alone for the
  // rest of this page load: a half-initialised SDK should not be called again. our own
  // server-side events do not go through the SDK, so they keep sending (df-3d12354a71ab).
  private mixpanelSdkFailed = false;
  // the error that set it, held until reportMixpanelSdkInitFailure() reports it. it cannot be reported from
  // the constructor: LoggingService is not yet connected to this service then, so its
  // report would reach Sentry but drop the sentry_error row and log a "Recursive error".
  private mixpanelSdkInitError: unknown = undefined;
  // a separate flag rather than "is the error undefined", so that even `throw undefined` is reported
  private mixpanelSdkInitReportPending = false;

  currentScreen: string = 'unknown';
  previousScreen: string = 'unknown';
  user: UserAuth = undefined;
  // uid whose session_start_authenticated was already sent in this app run
  private sessionStartTrackedForUid: string | undefined = undefined;
  trackingData: { [key: string]: any } = undefined; // this is filled only after the initialization, but we might be called before!

  private userDb: UserDb;
  private telemetryQueue: TelemetryQueue;
  // everything about HOW a payload reaches Mixpanel — see telemetry-transport.ts
  private telemetryTransport: TelemetryTransport;
  // everything about the Refiner survey tool — see refiner-client.ts
  private refiner: RefinerClient;
  private authService: AuthService;
  private configDb: ConfigDbService;

  constructor(
    private settings: SettingsService,
    private cloudFunctions: CloudFunctionsService,
    private onlineStateService: OnlineStateService,
    private pubSubService: PubSubService,
    private urlQuery: UrlQueryService,
    private logging: LoggingService,
  ) {
    this.adoptTrackingDataFromUrl();

    this.sessionId = getDeviceId();

    const mixPanelToken = AppEnvironment.mixPanelConfig.mixpanel_token;

    // decide ONCE, before the SDK is touched, whether this build may send anything at
    // all. the dev build the automated end-to-end runs use sends nothing, because those
    // events are billed like any other and nobody ever reads them. telemetry-mode.ts
    // explains the rule and why the local opt-in is shaped the way it is.
    this.telemetryMode = resolveTelemetryMode({
      environmentSendsTelemetry: AppEnvironment.sendsTelemetry,
      isAngularProduction: AppEnvironment.isAngularProduction,
      localOptInValue: readTelemetryLocalOptIn(typeof localStorage !== 'undefined' ? localStorage : undefined),
    });
    // say it exactly once, and when it is off say how to turn it on: a build that
    // silently sends nothing looks identical to a build whose telemetry is broken
    console.log(describeTelemetryMode(this.telemetryMode));

    if (telemetryModeSends(this.telemetryMode)) {
      this.initMixpanelSdk(mixPanelToken);
    }

    this.telemetryQueue = new TelemetryQueue(this.userDb, this.logging, this.cloudFunctions);
    this.telemetryTransport = new TelemetryTransport(
      this.telemetryQueue, this.onlineStateService, this.cloudFunctions, this.logging);
    this.refiner = new RefinerClient(this.settings, () => this.refinerSessionContext());

    // register to detect online/offline changes
    this.pubSubService.subscribe(connectionStateChangedEvent, (newState: ConnectionState) => {
      if (newState === ConnectionState.Online) {
        this.kickoffBackgroundFlusher();
      }
    });
  }

  /**
   * Take the tracking data from the address the app was loaded at, and fill in
   * cached campaign fields when that address carries none.
   *
   * Tracking data originally comes from the URL query, unless the user logs in
   * and the server's stored copy overrides it.
   */
  private adoptTrackingDataFromUrl(): void {
    this.trackingData = this.urlQuery.getTrackingData();

    // if URL has UTMs (fresh campaign visit), cache for future sessions
    // if URL has no UTMs (returning user/PWA), load cached UTMs as fallback before couchLogin returns
    if (hasCacheableTrackingFields(this.trackingData)) {
      cacheTrackingFields(this.trackingData);
    } else {
      const cached = loadCachedTracking();
      if (cached) {
        this.trackingData = { ...cached, ...this.trackingData };
      }
    }
  }

  /**
   * Take the tracking data from the address bar again, after the cold-start
   * deep-link path wrote a launch link's query into it.
   *
   * This service copies the tracking data when it is built, so refreshing the URL
   * service alone does not reach it. A magic link carries the campaign parameters
   * of the session that asked for it, and before the cold-start link was read in
   * place those reached us through the reload — so without this, the events we
   * record before somebody signs in would quietly lose them.
   *
   * The three mirrors of that bag are refreshed together, exactly as
   * `loginChanged` refreshes them when the server's copy arrives. Our own events
   * rebuild it live, but the SDK super-properties were registered once in the
   * constructor and the people-profile bag is cached — so re-reading without
   * re-stamping them would fix our events and quietly leave autocapture and
   * session-replay events carrying the pre-adoption campaign parameters.
   *
   * Boot-time only, and for the same reason `UrlQueryService.rereadAddressBar` is:
   * once a login has supplied the server's stored tracking data, re-deriving from
   * a URL would replace it with less.
   */
  refreshTrackingDataFromUrl(): void {
    this.adoptTrackingDataFromUrl();
    this.rebuildEnvironmentProps();
  }

  // everything that touches the Mixpanel SDK at start-up, in one place, so the
  // disabled build simply never calls it -- rather than calling it with a pile of
  // options set to off and trusting every one of them.
  //
  // if the SDK throws here, the throw must not leave this constructor: Angular could not
  // build the service and the whole app would show the "could not start" card, for an
  // analytics failure (df-3d12354a71ab, Sentry CYBERNETIC-FITNESS-APP-4HT: the SDK reads
  // `.search` on document.referrer, which some browsers leave undefined).
  private initMixpanelSdk(mixPanelToken: string): void {
    try {
      this.startMixpanelSdk(mixPanelToken);
    } catch (err) {
      this.mixpanelSdkFailed = true;
      this.mixpanelSdkInitError = err;
      this.mixpanelSdkInitReportPending = true;
    }
  }

  // report a failed SDK start-up once. app.component calls this right after
  // LoggingService.initialize, the first moment LoggingService can carry the whole report;
  // that runs for signed-out visitors too, unlike initialize() below.
  reportMixpanelSdkInitFailure(): void {
    if (!this.mixpanelSdkInitReportPending) {
      return;
    }
    const err = this.mixpanelSdkInitError;
    this.mixpanelSdkInitReportPending = false;
    this.mixpanelSdkInitError = undefined;
    this.logging.trackSilentProblem('MixpanelSdkInitFailed', {
      name: (err as any)?.name,
      message: (err as any)?.message,
    });
  }

  // the SDK calls themselves. only ever run through initMixpanelSdk's guard: called directly,
  // a throw would skip the failure flag and the next sdk() call would reach a broken SDK.
  private startMixpanelSdk(mixPanelToken: string): void {
    mixpanel.init(mixPanelToken, {
      // the relay accessor, not the waiting one (#4405): the Mixpanel SDK takes
      // its host once at init and this constructor cannot wait. Safe because
      // /api/t is a pure passthrough to the same Mixpanel project from either
      // server, so which one relays makes no difference to where events land.
      api_host: getUnversionedRelayOrigin() + '/api/t',
      debug: false,
      autocapture: false, // we handle events manually
      persistence: "localStorage",
      record_sessions_percent: 0, // don't record sessions by default (until user opts in)
      record_mask_text_selector: 'input[type="password"], input[type="email"], input[type="tel"]',
      // Unconditional, and limited by the replay dial rather than by a switch of its own.
      // The SDK only emits the four heatmap events ($mp_click, $mp_page_leave,
      // $mp_dead_click, $mp_rage_click) while a session recording is actually running --
      // every handler checks is_recording_heatmap_data(), which is "a replay is in
      // progress AND record_heatmap_data is on". So this being true sends no EVENTS on the
      // sessions we do not record, and ConfigKey.MixpanelReplayPercent is the one number
      // that governs both. There is deliberately no separate heatmap share: the two would
      // multiply, and a reader setting one of them would not see the other move.
      //
      // It is not free at runtime, though, which is worth knowing before assuming it is:
      // the listeners are installed on EVERY session, because Autocapture.init tests the
      // plain flag rather than is_recording_heatmap_data. That includes a document-wide
      // MutationObserver with a subtree config. Nothing is emitted and each callback only
      // writes a timestamp, but it is not nothing. This is the behaviour the app shipped
      // with for years, so it is a cost restored rather than a cost introduced.
      record_heatmap_data: true,
      // last chance to take a credential out of an SDK-emitted event's page address (#5151)
      hooks: { before_send_events: redactCredentialsInMixpanelEvent }
    } as MixpanelExtendedConfig);

    // this is the official way to override MixPanel SDK's default device ID
    // ref: https://docs.mixpanel.com/docs/session-replay/implement-session-replay/session-replay-web#mparticle-web-sdk
    mixpanel.register({ $device_id: this.sessionId, distinct_id: "$device:" + this.sessionId });

    // stamp environment context (app build, browser/OS, app_native/standalone,
    // trackingData) onto every SDK-emitted event — autocapture $mp_click /
    // $mp_page_leave, session-replay $mp_session_record — so they carry the
    // same attribution as our server-side telemetry path.
    this.registerEnvironmentSuperProps();
  }

  // The ONE place that decides whether the Mixpanel SDK may be touched. Every call into
  // the SDK goes through this. Not per-call guards: a mixpanel call made when we never
  // initialised the SDK writes a console error, and the end-to-end runs fail a build on
  // a console error -- so one missed guard on a future call site would be a red nightly
  // rather than a quiet no-op. It also answers null once the SDK failed to start.
  private sdk(): typeof mixpanel | null {
    return telemetryModeSends(this.telemetryMode) && !this.mixpanelSdkFailed ? mixpanel : null;
  }

  public cookiesProcessed(enableAnalytics: boolean) {

    this.setAnalyticsEnabled(enableAnalytics);

    if (enableAnalytics) {
      this.refiner.enableForProject();
    }
  }

  // how the app is being viewed: 'native' on a Capacitor native build,
  // 'standalone' on an installed PWA, 'browser' otherwise. shared so the Refiner
  // device traits and the Mixpanel profile bag always report the SAME value —
  // they must agree, which is the whole point of sending it. RefinerClient is
  // handed the answer (see RefinerSessionContext) rather than working it out again.
  private getAppDisplayMode(): 'native' | 'standalone' | 'browser' {
    return isCapacitorNative() ? 'native' : isAppStandalone() ? 'standalone' : 'browser';
  }

  // the parts of this session RefinerClient needs and must not cache: trackingData is
  // rewritten when a CouchDB login lands, and the user arrives after boot.
  private refinerSessionContext(): RefinerSessionContext {
    return {
      trackingData: this.trackingData,
      userId: this.user?.uid,
      appDisplayMode: this.getAppDisplayMode(),
      uiLanguage: this.getUiLanguageTelemetryProps(),
    };
  }

  public testSurvey() {
    this.refiner.showTestSurvey();
  }

  initialize(userDb: UserDb, authService: AuthService, configDb: ConfigDbService) {
    this.userDb = userDb;
    this.authService = authService;
    this.authService.userAuth.subscribe(user => this.loginChanged(user));
    // subscribes to couchAuthChanged, which replays synchronously — so this stays
    // exactly where the subscribe used to be (#4325)
    this.refiner.initialize(authService, configDb);
    this.telemetryQueue.initialize(userDb, this.logging);
    this.telemetryTransport.initialize(userDb, () => !this.authService.user);
    this.configDb = configDb;

    // If analytics were already enabled before initialize was called, start session recording
    if (!this.analyticsDisabled) {
      this.maybeStartSessionRecording();
    }

    // Cache Refiner events config early
    void this.refiner.cacheEventsAsync();

    // Load historical telemetry events from database
    void this.telemetryQueue.initAsync().then(() => {
      console.log('Telemetry: Historical events loaded from database');
      // Kick off the background flusher to process any loaded events
      this.kickoffBackgroundFlusher();
    }).catch(err => {
      console.error('Telemetry: Error loading historical events:', err);
    });
  }

  loginChanged(user: UserAuth) {
    const couchAuth = this.authService.couch;

    // if a user logs in and it has telemetry data saved from before
    if (user && couchAuth && couchAuth.telemetryData) {
      // use this telemetry data from now on
      let serverData = { ...couchAuth.telemetryData };
      // if server has 'direct' referrer but client has a real one, keep client's
      if (serverData['$referrer'] === 'direct' && this.trackingData?.['$referrer'] && this.trackingData['$referrer'] !== 'direct') {
        serverData['$referrer'] = this.trackingData['$referrer'];
        serverData['$referrer_domain'] = this.trackingData['$referrer_domain'];
      }
      this.trackingData = serverData;
      cacheTrackingFields(this.trackingData);
      // uncache the user-params bag (event bag is uncached, see getCommonEventParams) and
      // re-stamp the SDK super-props so autocapture/replay events see the updated
      // trackingData too (UTMs etc. arrive post-login via couchLogin)
      this.rebuildEnvironmentProps();
    }
  }

  // Decide whether to record THIS session, and apply that decision. One dial,
  // ConfigKey.MixpanelReplayPercent, governs both session replay and Mixpanel's heatmap
  // capture -- the SDK only emits the four heatmap events while a recording is running,
  // and we leave record_heatmap_data permanently on, so heatmap volume is exactly this
  // share of sessions. Raising the number therefore raises the event bill, not just the
  // replay count. See design-docs/telemetry.md, "How many sessions get recorded".
  //
  // THE DECISION IS DRAWN ONCE PER PAGE LOAD, and that is load-bearing. Two callers reach
  // this -- initialize() and setAnalyticsEnabled(true) -- and initialize() is itself
  // reached from two places in app.component.ts, so one page load really can arrive here
  // more than once. Drawing a fresh Math.random() each time would give the session a
  // second chance at being recorded: at 25% two draws record 43.75% of sessions, not 25%.
  //
  // That was invisible while the dial stood at 100, because `< 100` is always true, so
  // extra draws changed nothing. Lowering the dial is what made it matter, and it matters
  // twice over -- the number would stop meaning what it says, and heatmap capture rides on
  // the same sessions, so the event bill would run about three quarters above what the
  // configured percentage promises.
  //
  // Memoising the PROMISE rather than a boolean is what makes that safe: it is assigned
  // synchronously on the first call, so two callers in the same tick share one draw
  // instead of both racing past a flag that only gets set inside the async work. Later
  // callers re-apply the same answer, which is why a user who turns analytics off and
  // back on again in one page load resumes recording if this session was sampled, and
  // stays unrecorded if it was not. start_session_recording is idempotent in the SDK, so
  // re-applying costs nothing.
  private maybeStartSessionRecording() {
    if (!this.configDb) {
      console.log('Telemetry: configDb not initialized, skipping session recording check');
      return;
    }

    // assigned synchronously, before anything awaits -- see the comment above
    if (!this.sessionRecordingDecision) {
      this.sessionRecordingDecision = this.decideSessionRecordingAsync();
    }

    void this.sessionRecordingDecision.then(shouldRecord => {
      if (shouldRecord) {
        this.sdk()?.start_session_recording();
      }
    });
  }

  // Draw this page load's sampling decision. Separated from maybeStartSessionRecording so
  // that the memoised promise above holds the ANSWER rather than the side effect, which is
  // what lets a later caller re-apply it without drawing again.
  private async decideSessionRecordingAsync(): Promise<boolean> {
    const configured = await this.configDb.getConfigValueAsync<unknown>(ConfigKey.MixpanelReplayPercent);
    const { percent, malformed } = resolveReplayPercent(configured);

    // a configured value that could not be used is reported rather than silently
    // replaced by the default. an operator narrowing this dial during an incident, who
    // mistyped it, would otherwise see the app carry on and conclude the value took
    // effect -- and the whole point of the dial is that somebody can trust what it says.
    //
    // the problem tier is right -- the value is unresolved and an operator should act on
    // it -- but it is not the user's session going wrong: nothing they can see changed.
    // Counted, it would stop that person being offered the store review prompt on every
    // session for as long as the mistyped value sat there.
    if (malformed) {
      this.logging.trackSilentProblem('telemetry-replay-percent-unusable', {
        configured: JSON.stringify(configured),
        usedPercent: percent,
      }, undefined, SessionHealth.NotTheUsersSession);
    }

    // per SESSION, not per device: a heatmap and a replay corpus both want an unbiased
    // sample of sessions rather than everything a fixed cohort of people ever does.
    const shouldRecordSessions = Math.random() * 100 < percent;

    console.log(shouldRecordSessions
      ? `Telemetry: Starting session recording due to ${percent}% sampling`
      : `Telemetry: Skipping session recording due to ${percent}% sampling`);

    return shouldRecordSessions;
  }

  setAnalyticsEnabled(enabled: boolean) {
    // a build that may not send stays disabled whatever the cookie banner decided. this
    // is the single choke point for every one of our own events: the background flusher,
    // the direct relay POST and the queue's storage writes all read analyticsDisabled.
    //
    // It is written in exactly TWO places, both below and always to the same value: this
    // field, and the queue's copy. TelemetryTransport deliberately holds no third copy —
    // it reads the queue's, so there is nothing here to forget when a new reader appears.
    if (enabled && !telemetryModeSends(this.telemetryMode)) {
      console.log('Telemetry: consent given, but this build sends nothing — staying disabled.');
      this.analyticsDisabled = true;
      this.telemetryQueue.analyticsDisabled = true;
      return;
    }

    console.log('Telemetry: analytics enabled:', enabled);
    this.analyticsDisabled = !enabled;
    this.telemetryQueue.analyticsDisabled = !enabled;

    if (enabled) {
      this.telemetryQueue.syncToSessionStorage();
      this.maybeStartSessionRecording();
      this.kickoffBackgroundFlusher();
    } else {
      this.sdk()?.stop_session_recording();
    }
  }


  // runs on EVERY publish of a signed-in auth state: each app start with a stored
  // session, a real sign-in, and repeats such as a CouchDB login retry. so it must
  // not emit `login` — that fires only where a sign-in actually happens (login page,
  // one-time-token sign-in). it emits `session_start_authenticated` once per user
  // per app run instead. see design-docs/telemetry.md, "Sign-in versus session start".
  public trackAuthenticatedSession(user: UserAuth) {
    const wasTeamTestAccount = isTeamTestAccountEmail(this.user?.email);
    this.user = { ...user };
    // if team-test-account status changed (in either direction), the cached profile bag and
    // the SDK super-props carry the wrong test-traffic marker: rebuild both (#5043)
    if (isTeamTestAccountEmail(user.email) !== wasTeamTestAccount) {
      this.rebuildEnvironmentProps();
    }

    // if this user's session start was not recorded yet in this app run, record it
    if (shouldTrackAuthenticatedSessionStart(this.sessionStartTrackedForUid, user.uid)) {
      this.sessionStartTrackedForUid = user.uid;
      this.trackEvent('session_start_authenticated', { method: user.method });
    }

    this.refiner.identifyAtLogin(user);
  }


  public trackScreenEnter(screenName: string) {

    this.previousScreen = this.currentScreen;
    this.currentScreen = screenName;
    this.trackEvent('screen_enter', {}); // the screen names are automatically added

    this.refiner.trackScreen(screenName);
  }

  public setUserProperties(userProperties: { [key: string]: any }) {
    console.assert(this.user && typeof this.user.uid === 'string', 'User must be set before setting user properties');

    this.sdk()?.identify(this.user.uid);

    const commonUserProperties = this.getCommonUserParams();

    let loginTime = DateTime.utc()

    let setProperties = {
      $email: this.user.email,
      $name: this.user.name,
      $avatar: this.user.avatarUrl,
      login_method: this.user.method,
      ...userProperties,
      ...commonUserProperties,
      login_timestamp: loginTime.toISO(),
      login_timestamp_epoch: loginTime.toSeconds(),
    }

    let setOnceProperties: any = {};
    let unionProperties: any = {};
    for (let key in commonUserProperties) {
      setOnceProperties[key + "_first"] = setProperties[key];
      if (key !== 'login_timestamp') {
        unionProperties[key + "_all"] = setProperties[key];
      }
    }

    let userStateSet = encodeTelemetryUserState(this.user.uid, { set: setProperties });
    this.pushTelemetryData(userStateSet);
    let userStateSetFirst = encodeTelemetryUserState(this.user.uid, { set_once: setOnceProperties });
    this.pushTelemetryData(userStateSetFirst);
    let userStateUnion = encodeTelemetryUserState(this.user.uid, { union: unionProperties });
    this.pushTelemetryData(userStateUnion);
  }

  /**
   * Set user properties that should only be recorded once (first occurrence wins).
   * Useful for tracking "first time" events like first_workout_logged_at, first_checkin_at, etc.
   */
  public setUserPropertiesOnce(userProperties: { [key: string]: any }) {
    console.assert(this.user && typeof this.user.uid === 'string', 'User must be set before setting user properties');

    this.sdk()?.identify(this.user.uid);

    let userStateSetOnce = encodeTelemetryUserState(this.user.uid, { set_once: userProperties });
    this.pushTelemetryData(userStateSetOnce);

    // a set_once on its own tells the ActiveCampaign sync nothing, because last_modified must
    // never ride along in a set_once — it would freeze at its first value and hide the profile
    // from the sync for good. so send an otherwise-empty `set` alongside it: encodeTelemetryUserState
    // fills in a fresh last_modified (see its doc comment) and the sync observes the mutation.
    let userStateBumpModified = encodeTelemetryUserState(this.user.uid, { set: {} });
    this.pushTelemetryData(userStateBumpModified);
  }

  // Discriminated-property helper for the in-app update prompt's
  // `app_update` event family. Modeled on AppInstallService's
  // `trackAppInstallEvent` so the prop shape is set once (event,
  // surface, platform, current_version, available_version) instead
  // of being re-spelled at every emission site. Two surfaces emit:
  // - card: all four `event`s ('shown' on first render, 'clicked'
  //   on Update tap, 'dismissed' on the X, 'completed' on Android
  //   post-install via the AppUpdate listener).
  // - settings: only 'clicked' (one-tap action, no sheet to "show"
  //   or "dismiss").
  // - required_alert (#6770): the "Please update" alert shown below
  //   config/MinimumSupportedVersion. Home fires 'shown' when it opens it, 'clicked' on Update, 'dismissed' on Later or any other
  //   exit. Play's immediate flow restarts the app itself, so it never
  //   fires 'completed'.
  // `belowMinimum` is sent as `below_minimum` on every event: whether the
  // running build is below config/MinimumSupportedVersion, update or not.
  // Each caller passes it in; working it out here would need
  // VersionCheckService, which depends on this service (a cycle).
  // design-docs/in-app-update-prompt.md §Telemetry is the source of truth
  // for which event each surface emits.
  // `platform` is 'android' on Capacitor-Android, 'ios' on Capacitor-iOS
  // (the iTunes Lookup detector, Phase 2), 'web' on PWA. iOS never emits
  // a 'completed' event — Apple gives no install-completion signal, so
  // the card/settings handlers only fire 'shown' / 'clicked' / 'dismissed'
  // there (only the Android backend fires 'completed').
  public trackAppUpdateEvent(
    event: 'shown' | 'clicked' | 'dismissed' | 'completed',
    surface: 'card' | 'settings' | 'required_alert',
    availableVersion: string,
    belowMinimum: boolean,
  ): void {
    this.trackEvent('app_update', {
      event,
      surface,
      platform: this.updatePromptPlatform(),
      current_version: AppVersion,
      available_version: availableVersion,
      below_minimum: belowMinimum,
    });
  }

  // Maps the runtime to the `platform` dimension of the `app_update`
  // event family: 'android' / 'ios' on the respective Capacitor-native
  // builds, 'web' for the PWA.
  private updatePromptPlatform(): 'android' | 'ios' | 'web' {
    if (isCapacitorNative()) {
      if (isPlatformAndroid()) return 'android';
      if (isPlatformIOS()) return 'ios';
    }
    return 'web';
  }

  public trackEvent(eventName: string, params: { [key: string]: any }) {
    // only send to Refiner if event is in the configured list
    this.refiner.trackEventIfAllowed(eventName);

    // if this event means the user just logged food, remember it forever (#4325).
    // Fire-and-forget: it must never delay or fail the event itself.
    void this.markFoodLoggedEverAsync(eventName);

    let commonEventParams = this.getCommonEventParams();
    let ip = this.settings.locale && this.settings.locale.lastKnownIpInfo && this.settings.locale.lastKnownIpInfo.ip;

    let event = encodeTelemetryEvent(eventName, {
      ...commonEventParams,
      ...params,
      screen_name: this.currentScreen,
      previous_screen_name: this.previousScreen,
    },
      { userId: this.user && this.user.uid, ip: ip, deviceId: getDeviceId() });
    console.log(`Telemetry: event ${eventName} [${this.currentScreen}], params ${JSON.stringify(params)}`);

    this.pushTelemetryData(event);
  }

  /**
   * Flip the sticky "this user has logged food" marker the first time one of the
   * food-logging events fires (#4325).
   *
   * Called from trackEvent for EVERY event, so the decision is delegated to the
   * pure `shouldMarkFoodLoggedEver` (unit-tested in telemetry-profile-props.test.ts)
   * and this returns immediately in the overwhelmingly common case. Once the marker
   * is set it is a no-op for the rest of the account's life.
   *
   * Hooked centrally here rather than at the five call sites so the event list
   * lives in ONE place (telemetry-profile-props.ts) — a future sixth food-logging
   * path is covered the moment it emits one of these names.
   *
   * On the first hit it does three things: persist the marker, write the Mixpanel
   * people profile once (mirroring the existing first_workout_logged_at pattern),
   * and re-identify to Refiner so the survey segment updates in THIS session
   * rather than only at the next boot.
   */
  private async markFoodLoggedEverAsync(eventName: string): Promise<void> {
    const uid = this.user?.uid;
    if (!shouldMarkFoodLoggedEver({
      eventName,
      alreadyMarked: this.settings.hasLoggedFoodEver,
      isManagingClient: this.settings._isManagingClient,
      hasSignedInUser: !!uid,
      settingsLoaded: this.settings.isLoaded(),
    })) {
      return;
    }

    // set in memory first so a second food event in the same tick cannot double-fire,
    // then persist. The try covers ONLY the save, because the two failure kinds want
    // opposite handling: a failed save should let the next event retry, a failed
    // profile write should not. Both catches spell out why.
    this.settings.hasLoggedFoodEver = true;
    try {
      await this.settings.saveAsync();
    } catch (err) {
      // put the marker back so the user's NEXT food event retries the whole
      // sequence. Leaving it set would make the guard above swallow every later
      // attempt, and since the transition happens only once per user ever, the
      // marker would never reach CouchDB at all — stranding someone who just
      // logged food inside the "never logged food" survey audience.
      //
      // saveAsync writes localStorage BEFORE it awaits the CouchDB sync, so a
      // sync-side failure leaves localStorage already holding true while this puts
      // memory back to false. We revert anyway: retrying is what gets the value to
      // CouchDB, and the retry rewrites localStorage with the same true. That is
      // the opposite trade-off from the second catch below, where the value is
      // already safely persisted and reverting would only invite it to be undone.
      this.settings.hasLoggedFoodEver = false;
      this.logging.trackSilentProblem('markFoodLoggedEverFailed', {
        code: (err as any)?.code,
        message: (err as any)?.message,
      });
      return;
    }

    // the marker is persisted from here on, so a failure below must NOT revert it:
    // memory would then say false while localStorage and CouchDB say true, and the
    // next unrelated settings save would write that false back over them.
    try {
      // the Mixpanel half. Safe to write from the client (unlike the subscription
      // traits): these two properties are new, client-owned, and written at most
      // once per user, so they cannot churn the ActiveCampaign contact sync.
      this.setUserPropertiesOnce({
        has_logged_food_ever: true,
        first_food_logged_at: DateTime.utc().toISO(),
      });

      // re-identify so the Refiner segment updates in THIS session, not just at the
      // next boot. The trait is re-sent on every later identify regardless, so this
      // is the only piece a failure here actually costs.
      this.refiner.identifyFoodLogged(uid);
    } catch (err) {
      this.logging.trackSilentProblem('markFoodLoggedEverProfileWriteFailed', {
        code: (err as any)?.code,
        message: (err as any)?.message,
      });
    }
  }

  pushTelemetryData(data: TelemetryData) {
    // drop rather than queue when this build sends nothing. the flusher would refuse to
    // send it anyway, but queueing would grow the in-memory queue for the whole session
    // for data that can never leave. deliberately NOT in the trackEvent method, which
    // keeps running down to its console.log -- the end-to-end runs and manual debugging
    // read those lines, and they are how you can still see that an event fired.
    //
    // Write "the trackEvent method" rather than trackEvent with empty parentheses: the
    // telemetry-lexicon gate scans this file for that call shape and cannot tell a
    // mention in a comment from a real call site with an unreadable event name, so the
    // parenthesised form fails the push.
    if (!telemetryModeSends(this.telemetryMode)) {
      return;
    }

    this.telemetryQueue.pushData(data);

    this.kickoffBackgroundFlusher();
  }

  // The delivery pipeline lives in TelemetryTransport. These two stay here as delegates
  // because both still have callers: flushAsync from other modules, and
  // kickoffBackgroundFlusher from several places in this file.
  kickoffBackgroundFlusher() {
    this.telemetryTransport.kickoffBackgroundFlusher();
  }

  /** Flush telemetry and wait for completion. Returns a promise that resolves when flushing is done.
   *  If a flusher is already in progress, waits for it to finish instead of returning immediately. */
  async flushAsync(): Promise<void> {
    await this.telemetryTransport.flushAsync();
  }

  // single source of truth for environment context attached to events and people-
  // profile updates. consumed by:
  //   - getCommonEventParams()  → merged into our own server-side events
  //   - getCommonUserParams()   → set on Mixpanel people profile via setUserProperties()
  //   - registerEnvironmentSuperProps() → SDK super-props for autocapture/replay events
  // these are independent pipelines (see TelemetryTransport.callMixPanelAsync vs
  // SDK-direct $mp_* events), so each fans out from this same bag.
  private buildEnvironmentProps(): { [key: string]: any } {
    const browserInfo = Bowser.getParser(window.navigator.userAgent).getResult();
    const props: { [key: string]: any } = {
      '$app_build_number': AppVersion,
      app_standalone: isAppStandalone(),
      app_native: isCapacitorNative(),
      // #4981 phase 4 (#5270): WHICH ENGINE this device keeps its local PouchDB data in.
      //
      // It sits in this bag, rather than on the handful of events somebody might think to
      // add it to, because the question the rollout has to answer is a comparison: is
      // ANYTHING worse for the devices that moved? A dimension present on every event
      // answers that for any board somebody later thinks to look at; a dimension added per
      // event answers it only for the events somebody guessed in advance.
      //
      // Every platform reports it, not only Android. Saying "iOS is on SQLite and always
      // has been" is exactly the sort of thing a board cannot know, and a dimension that is
      // absent on some platforms is one nobody can safely group by.
      //
      // **It MUST be the non-latching read, and this is not a preference.** This bag is
      // built from the `TelemetryService` constructor, and rebuilt by every single
      // `trackEvent` — so it runs long before the boot hooks that migrate or roll back a
      // device. `getLocalDatabaseEngine()` latches on its first call, and both of those
      // hooks refuse to run once the latch is taken, so using it here would silently turn
      // the entire Android rollout off and then report `IndexedDb` for everybody: a
      // dimension measuring a rollout it had itself disabled. See
      // `peekLocalDatabaseEngineWithoutLatching`.
      local_database_engine: peekLocalDatabaseEngineWithoutLatching(),
      '$browser': browserInfo.browser.name,
      '$browser_version': browserInfo.browser.version,
      '$device': browserInfo.platform.type,
      '$manufacturer': browserInfo.platform.vendor,
      '$os': browserInfo.os.name,
      '$os_version': browserInfo.os.version,
      ...(this.trackingData || {}),
    };
    // if this session is test traffic, mark it so the analytics boards exclude it (#4098).
    // set here because this bag feeds ALL THREE client emission pipelines: our own events,
    // the people-profile updates, and the SDK super-props used by autocapture / replay.
    // written after the trackingData spread above, so the marker cannot be overwritten by it.
    // also when the signed-in account is one of the team's own (staff, testers): a check on
    // the live app carries no utm marker, so only the email tells it apart (#5043 follow-up)
    if (this.isTestTraffic()) {
      props[IS_TEST_TRAFFIC_PROPERTY] = true;
    }
    return props;
  }

  /**
   * Is this session test traffic — an E2E run, a browser opened with the `e2e-test`
   * marker, or one of the team's own accounts signed in?
   *
   * Public because Sentry asks the same question (df-a0223e8ffec8). Every Sentry event
   * carries the answer as a tag, read through the provider hook
   * `registerSentryTestTrafficProvider` that `LoggingService.initialize` registers — the
   * Sentry hooks run outside Angular DI and cannot inject this service. Having the two
   * sinks read ONE method is the point: "which traffic is test traffic" must not be able
   * to mean two different things depending on which system you look at.
   *
   * Live rather than cached, because both signals move during a session: the server's
   * stored tracking data arrives at login, and the team-account answer is unknown until
   * somebody signs in. `buildEnvironmentProps` is rebuilt on both of those, and the
   * Sentry hook asks per event.
   */
  public isTestTraffic(): boolean {
    return isTestTrafficSession(this.trackingData, isE2ETest())
      || isTeamTestAccountEmail(this.user?.email);
  }

  // push the environment bag into the SDK as super properties so SDK-emitted
  // events ($mp_click / $mp_page_leave / $mp_session_record) carry the same
  // context as our server-side telemetry. registering $browser / $browser_version
  // / $os / $os_version / $device / $manufacturer here intentionally overrides
  // the SDK's own UA-parser-derived defaults so all three emission sites agree
  // (Bowser is the single source of truth via buildEnvironmentProps).
  private registerEnvironmentSuperProps(): void {
    const props = this.buildEnvironmentProps();
    this.sdk()?.register(props);
    // if this session is not test traffic, drop a marker an earlier session left behind:
    // register only merges, and super-props persist in localStorage, so a team account
    // signed in on this browser before would otherwise mark every later user (#5043)
    if (!props[IS_TEST_TRAFFIC_PROPERTY]) {
      this.sdk()?.unregister(IS_TEST_TRAFFIC_PROPERTY);
    }
  }

  // forget the cached profile bag and re-push the SDK super-props, after anything that
  // changes what buildEnvironmentProps returns (tracking data, the signed-in account)
  private rebuildEnvironmentProps(): void {
    this.commonUserParams = undefined;
    this.registerEnvironmentSuperProps();
  }

  // build the per-event env bag (app build, browser/OS, app_native/standalone,
  // trackingData) plus the current session-replay id if recording is active.
  // not cached: $mp_replay_id is fetched at call time (it changes when session
  // recording starts/stops mid-session) and the rest of the bag is a cheap
  // Bowser parse + 9 keys, so per-event reconstruction is fine.
  getCommonEventParams(): any {
    let commonEventParams = this.buildEnvironmentProps();

    // if session-recording is active, attach the current replay id
    let mixpanelSessionProps: { $mp_replay_id?: string } = this.sdk()?.get_session_recording_properties();
    if (mixpanelSessionProps && mixpanelSessionProps.$mp_replay_id) {
      commonEventParams['$mp_replay_id'] = mixpanelSessionProps.$mp_replay_id;
    }

    // #3992 (offline-mode Phase 4): standard offline-health properties on
    // every event, so any funnel can be sliced by connectivity. Deliberately
    // in this LIVE per-event bag, not the cached user bag — both values
    // change during a session. the sessionStorage read mirrors the
    // timeSinceLastSync pattern in database-wrapper.ts (no import needed).
    commonEventParams['connection_state'] = this.onlineStateService.connectionState;
    commonEventParams['minutes_since_last_sync'] = sessionStorage.lastUserDbSyncTime_utc
      ? Math.round(DateTime.utc().diff(DateTime.fromISO(sessionStorage.lastUserDbSyncTime_utc)).as('minutes'))
      : null;

    return commonEventParams;
  }

  commonUserParams: { [key: string]: any } | undefined = undefined;
  // build the env bag for Mixpanel people-profile updates. cached per session
  // and invalidated by loginChanged() when server-side trackingData arrives.
  getCommonUserParams(): any {
    if (!this.commonUserParams) {
      this.commonUserParams = this.buildEnvironmentProps();
    }
    return this.commonUserParams;
  }

  // fire-and-forget entry point that writes the user's Mixpanel profile properties.
  // called at boot and at intake completion; failures are reported instead of
  // swallowed (see sendUserProfileAsync for the actual work).
  public setUserInfo(userDb: UserDb, user: UserAuth, settings?: SettingsService) {
    void (async () => {
      try {
        await this.sendUserProfileAsync(userDb, user, settings);
      } catch (err) {
        // profile properties only enrich analytics, so the user is unaffected — but
        // losing them silently skews every profile-based Mixpanel breakdown (this
        // exact silent failure hid a whole cohort of one-session native trial users,
        // 2026-07). report and continue.
        this.logging.trackSilentProblem('setUserInfoFailed', {
          code: (err as any)?.code,
          message: (err as any)?.message,
        });
      }
    })();
  }

  // build the raw UI-language signals (#4147): active app language (what LL is
  // bound to for this session), the device's raw browser language, and the
  // country-default language from the last geo lookup. Reported on the profile
  // and on the language_changed event — never stamped on every event. The geo
  // language list comes from the same lastKnownIpInfo the IP is read from above.
  getUiLanguageTelemetryProps(): UiLanguageTelemetryProps {
    const countryLanguages = this.settings.locale?.lastKnownIpInfo?.languages;
    return buildUiLanguageTelemetryProps(BOOT_LOCALE, { countryLanguages });
  }

  // builds and sends the Mixpanel people-profile properties for the logged-in user.
  // called at boot (app.component) and again when the intake form finishes, because
  // most of the properties only exist once intake is done.
  private async sendUserProfileAsync(userDb: UserDb, user: UserAuth, settings?: SettingsService) {
    const [immutable, fitnessState, fitnessPlan, declaredState] = await userDb.database.withDocWatcherAsync(
      watcher => Promise.all([
        userDb.loadImmutableStateAsync(watcher),
        userDb.loadFitnessStateAsync(watcher),
        userDb.loadPlanAsync(watcher),
        userDb.loadDeclaredStateAsync(watcher),
      ]));

    // build the props with the pure helper: before intake is done it returns only
    // the props that exist without intake answers (display mode + notification
    // toggles) — sending those is a normal state, not an error, and the intake
    // form calls setUserInfo again when it finishes. after intake it returns the
    // full derived set. see telemetry-profile-props.ts.
    const userProperties = buildUserProfileProps({
      immutable,
      fitnessState,
      fitnessPlan,
      declaredState,
      appDisplayMode: this.getAppDisplayMode(),
      notificationSettings: settings,
      uiLanguage: this.getUiLanguageTelemetryProps(),
      now: DateTime.utc(),
    });

    // TODO We should reconcile this. setUserProperties and setUserInfo are separate only because one was previously in FirebaseService.
    this.setUserProperties(userProperties);

    // if the user has finished intake and created a plan, the engagement bag is
    // worth forwarding too — before that it holds only display mode and the
    // notification toggles
    const intakeFinished = isIntakeDone(immutable) && fitnessPlan.ready;

    // repeat user identification for Refiner — the reasoning for why it happens
    // here and what it carries lives with the call, in refiner-client.ts.
    this.refiner.identifyWithProfile(user, intakeFinished, fitnessPlan, userProperties);
  }
}
